import { t } from "@i18n";
import { presetSectionsCodeObject, SECTION_TYPE_TABLE_SECTION, formCategories } from "@Helpers";
import { getPresetSectionsObject } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/constants";
import type { QuestionField } from "@ServerTypes/forms/forms.schema";
import type { FormInstance, FormInstanceSection } from "../../formInstance.wire";

/**
 * One rule bag as `useFormValidator` (FormValidation/useFormValidator.tsx) consumes it. The four
 * bounds copied straight off the question come from `questionFieldSchema`; `required` /
 * `min|maxLength` / `min|maxRange` are the validator's own names for `isMandatory` /
 * `min|maxFieldLimit` / `min|maxFieldRange`. Kept in step with the `FieldRules` type declared next
 * to the validator itself — that one is local to that file, so it cannot be imported here.
 */
type FieldValidationRule = Pick<QuestionField, "minFieldRange" | "maxFieldRange" | "minNoOfFiles" | "maxNoOfFiles"> & {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  minRange?: number;
  maxRange?: number;
  /**
   * Only the two date-time scopes the validator branches on — `getDateTimeRules` below copies
   * `selectionScope` across only when it is one of these two. Must stay identical to the
   * `Extract<…>` in `useFormValidator`'s `FieldRules`: `updateRules` takes the rule bag as a
   * parameter, so the two types are compared contravariantly and any widening here breaks every
   * caller. Duplicated rather than imported because that file declares its rule types locally.
   */
  selectionScope?: Extract<QuestionField["selectionScope"], "futureDateTime" | "pastDateTime">;
  /** The current answer, echoed into the rule so `validateAll` can re-run without the field. */
  value?: unknown;
};

/**
 * One row of a table section, as `formTemplateFieldSchema.tableDataSource` stores them: a map of
 * question `_id` → captured answer, plus the row's own `rowId`. The schema types the array
 * `z.array(z.any())`, so this is the narrowest shape the readers here actually rely on.
 */
export type TableSectionRow = { rowId?: string | number } & Record<string, { value?: unknown } | undefined>;

/**
 * The per-level Fill/Modify/Verify user-id lists an instance carries on each of its template
 * sections. `inspectionAndProgress.collection.ts` models this as `z.record(z.string(), z.any())`, so
 * the inner shape is untyped at the source — reported as a server-schema gap rather than re-declared
 * here, because a guess made in web would not be enforced anywhere.
 */
export type SectionPermissions = FormInstanceSection["permissions"];

/**
 * Coerce a raw form-field value to a finite number, or `null` when the value is
 * blank/undefined/non-numeric. Used to build a numeric scope for formula
 * evaluation so a referenced-but-empty field leaves the formula incomplete
 * (evaluates to `null`) instead of being silently treated as `0`.
 */
export const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * Join the resolved values of `selectedFields` (in order) using `seperator`.
 * Falsy values are skipped; returns "" when nothing resolves.
 */
export const buildConcatenatedValue = (
  selectedFields: Array<string> | undefined,
  valueById: Map<string, string>,
  seperator: string | undefined,
): string =>
  (selectedFields ?? [])
    .map((id) => valueById.get(id))
    .filter(Boolean)
    .join(seperator ?? "");

export const TEMPLATE_SOURCE_MAIL_CREATION = "MAIL_CREATION";
export const TEMPLATE_SOURCE_MAIL_SIDEBAR = "MAIL_SIDEBAR";

export const preSubmissionTypeDataSource = [
  { name: "Fill", value: "Fill" },
  { name: "Modify", value: "Modify" },
];
export const postSubmissionTypeDataSource = [
  { name: "Fill", value: "Fill" },
  { name: "Modify", value: "Modify" },
  { name: "Verify", value: "Verify" },
];
export const linkBoqSources = {
  INSPECTION_FORM: "inspectionForms",
  LINK_ITEMS: "linkItems",
};
export const formStatuses = {
  inProgress: "IN_PROGRESS",
  rejected: "REJECTED",
  approved: "APPROVED",
  submitted: "SUBMITTED",
};
export const formStatusFilterDataSource = [
  { name: "In Progress", value: formStatuses.inProgress },
  { name: "Rejected", value: formStatuses.rejected },
  { name: "Approved", value: formStatuses.approved },
  { name: "Submitted", value: formStatuses.submitted },
];

export const formSectionType = {
  FORM: "form",
  TABLE_SECTION: "tableSection",
};
export const getUserPermissionForForm = (
  inspectionData: FormInstance,
  // Current user's id; may be absent before the session loads — `.includes(undefined)` then
  // safely yields no-permission.
  userId: string | undefined,
  sectionIndex: number,
): boolean => {
  const isCurrentUserAllowed =
    // Three erased assertions per branch, none of which changes what runs:
    // `template[sectionIndex]` is `T | undefined` only because of `noUncheckedIndexedAccess` (the
    // caller resolved the index from the same array); `permissions` and `isArchived` are the
    // KNOWN BUGs documented on `getSectionPermission` / `getPermissions` below.
    (inspectionData.selectedForm.template[sectionIndex]!.permissions![inspectionData.currentLevelOfForm].Fill.includes(
      userId,
    ) ||
      inspectionData.selectedForm.template[sectionIndex]!.permissions![
        inspectionData.currentLevelOfForm
      ].Modify.includes(userId)) &&
    !inspectionData.isRejected &&
    !inspectionData.isApproved &&
    !inspectionData.isArchived!.status;

  return isCurrentUserAllowed;
};
export const getFillPermissionForForm = (
  inspectionData: FormInstance,
  userId: string | undefined,
  sectionIndex: number,
): boolean => {
  const isCurrentUserAllowed =
    !inspectionData.isRejected &&
    !inspectionData.isApproved &&
    !inspectionData.isArchived!.status &&
    inspectionData.selectedForm.template[sectionIndex]!.permissions![inspectionData.currentLevelOfForm].Fill.includes(
      userId,
    );

  return isCurrentUserAllowed;
};
export const getModifyPermissionForForm = (
  inspectionData: FormInstance,
  userId: string | undefined,
  sectionIndex: number,
  // Section ids the user has opened for modification. Declared as possibly holding `undefined`
  // because the ids it is tested against are `.optional()` on the wire; a real caller passes
  // `string[]`, which is assignable.
  modifySection: Array<string | undefined> | undefined,
): boolean | undefined => {
  const sectionId = inspectionData.selectedForm?.template[sectionIndex]?._id;

  const isCurrentUserAllowed =
    !inspectionData.isRejected &&
    !inspectionData.isApproved &&
    !inspectionData.isArchived!.status &&
    inspectionData.selectedForm.template[sectionIndex]!.permissions![inspectionData.currentLevelOfForm].Modify.includes(
      userId,
    ) &&
    !inspectionData.isRejected &&
    !inspectionData.isApproved &&
    !inspectionData.isArchived!.status &&
    modifySection?.includes(sectionId);

  return isCurrentUserAllowed;
};

export const getSectionPermission = ({
  section,
  inspectionData,
  userId,
  type,
}: {
  section: FormInstanceSection;
  inspectionData: FormInstance;
  // Current user's id; may be absent before the session loads — `.includes(undefined)` then
  // safely yields no-permission.
  userId: string | undefined;
  type: string;
}): boolean => {
  // KNOWN BUG, preserved verbatim: `permissions` is `.optional()` on the wire (a template-authored
  // section that has never been through a form flow carries none) but is dereferenced
  // unconditionally here, so such a section throws a TypeError. Asserted (erased at emit) to keep
  // the existing runtime; the real fix is a guard plus a no-permission path — a behaviour change,
  // out of scope for a typing pass. Same for `isArchived`, which is `.optional()` on the instance
  // yet read as `isArchived.status` here and as `isArchived?.status` in `getPermissions` below.
  const isCurrentUserAllowed =
    section.permissions![inspectionData.currentLevelOfForm][type].includes(userId) &&
    !inspectionData.isArchived!.status &&
    !inspectionData.isRejected &&
    !inspectionData.isApproved;

  return isCurrentUserAllowed;
};
export const getPermissions = ({
  inspectionData,
  permissions = {},
  type,
  userId,
}: {
  // `Partial` because the template-authoring and mailbox-sidebar paths call this with a form
  // *template* rather than an instance — the `?.` reads below are what make that work.
  inspectionData: Partial<FormInstance>;
  permissions: SectionPermissions;
  type: string;
  userId: string;
}): boolean => {
  if (typeof permissions == "object" && !Object.keys(permissions).length) {
    return true;
  }
  if (inspectionData.isApproved || inspectionData.isRejected || inspectionData.isArchived?.status) {
    return false;
  }
  // `currentLevelOfForm!` is an index-position assertion, erased at emit: on a template object the
  // property is absent, and `permissions[undefined]` resolves to `permissions["undefined"]` →
  // `undefined` → the `?.` chain short-circuits, exactly as before.
  return !!permissions?.[inspectionData.currentLevelOfForm!]?.[type]?.includes(userId);
};

export const getUrlFromField = (url: string) => {
  const newUrl = /^(https?:)?\/\//i.test(url) ? url : `https://${url}`;
  return newUrl;
};

const getTextRules = (field: QuestionField): FieldValidationRule => {
  return {
    minLength: field.minFieldLimit ?? 0,
    ...(field.maxFieldLimit && { maxLength: field.maxFieldLimit }),
  };
};

const getNumberRules = (field: QuestionField): FieldValidationRule => {
  return {
    minRange: field.minFieldRange ?? 0,
    ...(field.maxFieldRange && { maxRange: field.maxFieldRange }),
  };
};

const getFileRules = (field: QuestionField): FieldValidationRule => {
  return {
    ...(field.maxNoOfFiles && { maxNoOfFiles: field.maxNoOfFiles }),
    ...(field.minNoOfFiles && { minNoOfFiles: field.minNoOfFiles }),
  };
};

const getDateTimeRules = (field: QuestionField): FieldValidationRule => {
  const rules: FieldValidationRule = {
    ...(field.maxFieldRange && { maxFieldRange: field.maxFieldRange }),
    ...(field.minFieldRange && { minFieldRange: field.minFieldRange }),
  };
  if (["futureDateTime", "pastDateTime"].includes(field.selectionScope as string)) {
    // Both casts are erased. `.includes` does not narrow, but it has already established that the
    // value is one of the two scopes the rule type admits, so the assertion states what the guard
    // above proved.
    rules.selectionScope = field.selectionScope as FieldValidationRule["selectionScope"];
  }
  return rules;
};

const generateRuleObj = (eachField: QuestionField, row?: TableSectionRow): FieldValidationRule => {
  const value = (row ? row[eachField._id]?.value : eachField.answer?.value) ?? "";

  let ruleObj: FieldValidationRule = {};

  if (eachField.isMandatory) {
    ruleObj.required = true;
  }

  switch (eachField.fieldType) {
    case "single-line":
    case "multi-line":
      ruleObj = { ...ruleObj, ...getTextRules(eachField) };
      break;
    case "number":
      ruleObj = { ...ruleObj, ...getNumberRules(eachField) };
      break;
    case "fileUpload":
    case "image":
      ruleObj = { ...ruleObj, ...getFileRules(eachField) };
      break;
    case "date-time":
      ruleObj = { ...ruleObj, ...getDateTimeRules(eachField) };
      break;
  }

  return { ...ruleObj, value };
};

export function addValidationToForms({
  formValidator,
  template = [],
  inspectionData,
  rows,
  modifySection = [],
  templateSource,
  resourceType,
}: {
  formValidator: { updateRules: (rules: Record<string, FieldValidationRule>) => void };
  template?: FormInstanceSection[];
  inspectionData?: FormInstance;
  /** Bulk-edit rows (file upload grid): the rule key is prefixed with each row's id. */
  rows?: Array<{ id?: string | number }>;
  modifySection?: Array<string | undefined>;
  templateSource?: string;
  resourceType?: string;
}) {
  const isMailSidebarContext =
    resourceType === formCategories.mailBox && templateSource === TEMPLATE_SOURCE_MAIL_SIDEBAR;

  const allSectionsInCurrentLevel: Array<string | undefined> = [];
  if (inspectionData?._id) {
    // `formFlow[currentLevelOfForm]` is `T | undefined` only under `noUncheckedIndexedAccess`, and
    // `dataSource` / `sections` are `.optional()` on the wire but read unconditionally here. Both
    // assertions are erased at emit; see the section-permission KNOWN BUG note above for the same
    // pattern on `permissions`.
    inspectionData.formFlow[inspectionData.currentLevelOfForm]!.dataSource!.map((data) => {
      if (data.type === "Fill") {
        data.sections!.map((section) => allSectionsInCurrentLevel.push(section._id));
      } else if (data.type === "Modify") {
        data.sections!.map((section) => {
          if (modifySection.includes(section._id)) {
            allSectionsInCurrentLevel.push(section._id);
          }
        });
      }
    });
    template = inspectionData.selectedForm.template;
  } else if (template?.length) {
    template?.forEach((section) => {
      allSectionsInCurrentLevel.push(section._id);
    });
  }
  const options: { rules: Record<string, FieldValidationRule> } = { rules: {} };
  template?.forEach((section) => {
    if (
      section.sectionCode === presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION &&
      allSectionsInCurrentLevel.includes(section._id)
    ) {
      // `questionFields` / `tableDataSource` are `.optional()` on the wire and read unconditionally
      // in this branch; erased assertions keep that.
      section.questionFields!.forEach((eachField) => {
        section.tableDataSource!.forEach((row: TableSectionRow) => {
          const ruleName: string = `${row.rowId}${eachField._id}`;
          const rulesObj: FieldValidationRule = generateRuleObj(eachField, row);
          if (isMailSidebarContext && !eachField.isMetadata) {
            delete rulesObj.required;
          }
          options.rules[ruleName] = {
            ...rulesObj,
          };
        });
      });
    } else if (section.questionFields && allSectionsInCurrentLevel.includes(section._id)) {
      section.questionFields.forEach((eachField) => {
        const ruleName = `${eachField._id}`;
        const rulesObj: FieldValidationRule = generateRuleObj(eachField);
        if (isMailSidebarContext && !eachField.isMetadata) {
          delete rulesObj.required;
        }
        if (rows) {
          rows.forEach((row) => {
            options.rules[`${row.id}_${ruleName}`] = {
              ...rulesObj,
            };
          });
        } else {
          options.rules[ruleName] = {
            ...rulesObj,
          };
        }
      });
    }
  });
  formValidator.updateRules(options.rules);
}

export function checkMandatoryFields(inspectionData: FormInstance) {
  const allSectionsInCurrentLevel: Array<string | undefined> = [];
  // Same three erased assertions as in `addValidationToForms`: index access under
  // `noUncheckedIndexedAccess`, plus `dataSource` / `sections` being `.optional()` on the wire yet
  // dereferenced unconditionally here.
  inspectionData.formFlow[inspectionData.currentLevelOfForm]!.dataSource!.map((data) => {
    data.sections!.map((section) => allSectionsInCurrentLevel.push(section._id));
  });
  let errorToastContent = t("Form submission failed: Not all mandatory fields filled.");
  let isAllMandatoryFieldsFilled = true;

  const isEmptyValue = (value: unknown) => {
    if ((["", undefined, null] as unknown[]).includes(value)) return true;
    if (Array.isArray(value)) return value.join("").trim() === "";
    // `null` was already returned above, so the widening to `object` is safe and erased.
    if (typeof value === "object" && Object.keys(value as object).length === 0) return true;
    return false;
  };

  const validateMandatory = (isMandatory: boolean | undefined, value: unknown) => {
    if (isMandatory && isEmptyValue(value)) {
      isAllMandatoryFieldsFilled = false;
      return true;
    }
    return false;
  };

  const validateTextField = (field: QuestionField, value: string | undefined) => {
    if (!["single-line", "multi-line"].includes(field.fieldType) || !value?.length) return false;

    if (field.minFieldLimit && field.minFieldLimit > value.length) {
      errorToastContent = t(`Form submission failed: Minimum characters criteria not met`);
      isAllMandatoryFieldsFilled = false;
      return true;
    }
    if (field.maxFieldLimit && value.length > field.maxFieldLimit) {
      errorToastContent = t(`Form submission failed: Entered value exceeds the max characters set for this field`);
      isAllMandatoryFieldsFilled = false;
      return true;
    }
    return false;
  };

  const validateDateTime = (field: QuestionField, value: number | undefined) => {
    if (field.fieldType !== "date-time") return false;

    if (field.selectionScope === "pastDateTime" && value && value > Date.now()) {
      errorToastContent = t("Form submission failed: Please select a date and time from the past");
      isAllMandatoryFieldsFilled = false;
      return true;
    }

    if (
      field.selectionScope === "rangeBased" &&
      ((field.minFieldRange && value && value < field.minFieldRange) ||
        (field.maxFieldRange && value && value > field.maxFieldRange))
    ) {
      errorToastContent = t("Form submission failed: Please select a date and time within the predefined range");
      isAllMandatoryFieldsFilled = false;
      return true;
    }

    return false;
  };

  const validateFiles = (field: QuestionField, value: unknown) => {
    if (!Array.isArray(value) || !value.length) return false;

    if (field.minNoOfFiles && field.minNoOfFiles > value.length) {
      errorToastContent = t(`Form submission failed: Minimun files criteria not met`);
      isAllMandatoryFieldsFilled = false;
      return true;
    }

    if (field.maxNoOfFiles && value.length > field.maxNoOfFiles) {
      isAllMandatoryFieldsFilled = false;
      errorToastContent = t("Form submission failed: Files uploaded exceeds the max limit set for this field");
      return true;
    }

    return false;
  };

  const generateRule = (eachField: QuestionField, row?: TableSectionRow) => {
    const answerValue: unknown = (row ? row[eachField._id]?.value : eachField.answer?.value) ?? "";

    // The per-fieldType casts are erased. Each validator re-checks `fieldType` before touching a
    // shape-specific member, so the cast only tells the compiler which branch it is in.
    return (
      validateMandatory(eachField.isMandatory, answerValue) ||
      validateTextField(eachField, answerValue as string | undefined) ||
      validateDateTime(eachField, answerValue as number | undefined) ||
      validateFiles(eachField, answerValue)
    );
  };

  inspectionData.selectedForm.template?.some((section) => {
    if (
      section.sectionCode == presetSectionsCodeObject.SECTION_TYPE_TABLE_SECTION &&
      allSectionsInCurrentLevel.includes(section._id)
    ) {
      section.questionFields!.some((eachField) => {
        if (section?.tableDataSource?.length) {
          section.tableDataSource.some((row: TableSectionRow) => {
            return generateRule(eachField, row);
          });
        } else {
          return generateRule(eachField);
        }
      });
    } else if (section.questionFields && !section.sectionCode && allSectionsInCurrentLevel.includes(section._id)) {
      section.questionFields.some((eachField) => {
        return generateRule(eachField);
      });
      return !isAllMandatoryFieldsFilled;
    }
    return false;
  });
  return {
    errorToastContent,
    isAllMandatoryFieldsFilled,
  };
}
export const formStatusMapper: Record<string, string> = {
  [formStatuses.inProgress]: "In Progress",
  [formStatuses.rejected]: "Rejected",
  [formStatuses.approved]: "Approved",
  [formStatuses.submitted]: "Submitted",
};
export function getSectionName(section: { sectionCode?: string | null; sectionHeading?: string }) {
  // Widened to a string-keyed map: the literal returned by `getPresetSectionsObject` has literal
  // keys, which cannot be indexed by a plain `string`. Annotation only — no runtime change.
  const presetSectionsObject: Record<string, string> = getPresetSectionsObject();

  if (
    section.sectionCode &&
    section.sectionCode != SECTION_TYPE_TABLE_SECTION &&
    presetSectionsObject[section.sectionCode]
  ) {
    return presetSectionsObject[section.sectionCode];
  }
  return section.sectionHeading;
}
